import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { Settings } from 'luxon';
import { installSegmentSanitizationMiddleware, shouldLoadSegmentAnalytics } from './app/services/segment/analytics-init';
import { stripSensitiveParamsFromBrowserUrl } from './app/util/analytics-url';
import { AppModule } from './app/app/app.module';
import { environment } from './environments/environment';
import { hmrBootstrap } from './hmr';
import './sentry';

Settings.defaultLocale = 'en-US';

declare const module;

if (environment.production) {
  enableProdMode();
}

const bootstrap = () => platformBrowserDynamic().bootstrapModule(AppModule);
if (environment.hmr) {
  if (module.hot) {
    hmrBootstrap(module, bootstrap);
  } else {
    console.error('HMR is not enabled for webpack-dev-server!');
    console.log('Are you using the --hmr flag for ng serve?');
  }
} else {
  bootstrap().catch(err => console.log(err));
}

stripSensitiveParamsFromBrowserUrl();

if (shouldLoadSegmentAnalytics(environment.enableAnalytics)) {
  installSegmentSanitizationMiddleware(window.analytics);
  window.analytics?.load(environment.env.TD_APP_SEGMENT_KEY);
}

// NOTE: the Amplitude QA console logger is installed reactively in AppComponent,
// driven solely by the GrowthBook `amplitude-dev-logger-flag`. It is intentionally
// not installed here so toggling the flag (on any env) takes effect without a reload.

loadHubspotScript();
loadHubspotFormScript();
loadUseberryScript();
loadCelloScript();

export function loadScript(src: string, appendTo: 'head' | 'body', beforeAppend?: (el: HTMLScriptElement) => void) {
  const script = document.createElement('script');
  script.src = src;
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  beforeAppend?.(script);

  document[appendTo].appendChild(script);
}

function loadHubspotScript() {
  const hubspotKey = environment.hubspotKey;
  if (!hubspotKey) return;
  const src = `//js.hs-scripts.com/${hubspotKey}.js`;
  loadScript(src, 'head');
}

function loadHubspotFormScript() {
  const publicDeployments = ['uspublic', 'staging'];
  const isPublicDeployment = publicDeployments.includes(environment.deployment);
  if (!isPublicDeployment) return;
  const src = 'https://js.hsforms.net/forms/embed/v2.js';
  loadScript(src, 'head', (script) => {
    script.async = true;
  });
}

function loadUseberryScript() {
  const publicDeployments = ['uspublic', 'staging'];
  const isPublicDeployment = publicDeployments.includes(environment.deployment);
  if (!isPublicDeployment) return;
  const src = 'https://api.useberry.com/integrations/liveUrl/scripts/useberryScript.js';
  loadScript(src, 'body');
}

function loadCelloScript() {
  // Initialize Cello command queue before script loads
  window.cello = window.cello || { cmd: [] };

  // Load Cello JS SDK - replace with actual CDN URL from Cello dashboard
  const celloScriptUrl = environment.env.TD_APP_CELLO_SCRIPT_URL;
  if (celloScriptUrl) {
    loadScript(celloScriptUrl, 'head', (script) => {
      // Cello script uses ES6 modules, so we need to set type="module"
      script.type = 'module';
    });
  }
}
